/**
 * An organizer started from last year's return: what the filed return shows about the client's
 * situation becomes answers the client confirms ("Your 2025 return shows business income on
 * Schedule C."). Only unanswered questions marked to carry forward (`prefill`: ongoing situations
 * such as a mortgage or a rental) are answered; questions about what happened this year (care paid,
 * sales, distributions) get a note only. Something missing from last year's return says nothing
 * about this year, so it is never a "No". Lists (dependents, rentals, mortgages) get a note, not
 * rows. Notes carry no amounts. Works with the standard organizer and the form library's 1040
 * organizer (their keys differ).
 */
import { visibleQuestions, type AnswerValue, type Answers, type OrganizerDefinition, type Question } from "./organizer";

/** One question started from the return: its key and why (shown to the client and staff). */
export type ReturnHint = { key: string; note: string };

/** Lines read from the return, as printed (text), by key (see ORGANIZER_RETURN_LINES in the API). */
export type ReturnLines = Record<string, string | null | undefined>;

/** A printed amount is there and not zero ("1,234", "(56)", "-7" count; "0", "-0-", blank don't). */
export function hasAmount(v: string | null | undefined): boolean {
  if (!v) return false;
  const digits = v.replace(/[^0-9.]/g, "");
  return digits !== "" && Number(digits) !== 0;
}
const isYes = (v: string | null | undefined) => !!v && /^\s*y(es)?\b/i.test(v);
const status = (l: ReturnLines) => l.filingStatus?.trim().toLowerCase() ?? "";
/** The library organizer words its refund choice differently. */
const CHOICE_ALIASES: Record<string, string> = { direct_deposit: "deposit" };

/** The answer for each question key it can fill (null: a note only, the question is about this year or is a list). */
type Targets = Record<string, ((l: ReturnLines, q: Question) => AnswerValue | undefined) | true | null>;
type Rule = { test: (l: ReturnLines) => boolean; note: string; targets: Targets };

const RULES: Rule[] = [
  {
    test: (l) => ["single", "mfj", "mfs", "hoh", "qss"].includes(status(l)),
    note: "the filing status you used",
    targets: {
      // The standard organizer asks for the filing status itself; the library's asks for marital
      // status on December 31, where only a married filing status says it (Single also covers
      // divorced, separated and widowed). Qualifying surviving spouse lasts two years only, so it is
      // never carried forward.
      filing_status: (l, q) => {
        const v = status(l);
        const marital = (q.options ?? []).some((o) => o.value === "married");
        return marital ? (v === "mfj" || v === "mfs" ? "married" : undefined) : ({ single: "single", mfj: "mfj", mfs: "mfs", hoh: "hoh" } as Record<string, string>)[v];
      },
    },
  },
  { test: (l) => hasAmount(l.dependents), note: "dependents were claimed", targets: { has_dependents: true, dependents: null } },
  { test: (l) => isYes(l.childCare), note: "child and dependent care expenses (Form 2441)", targets: { childcare: true, child_care: true } },
  { test: (l) => hasAmount(l.wages), note: "wages (Forms W-2)", targets: { w2_count: null, w2s: null } },
  { test: (l) => hasAmount(l.taxableInterest) || hasAmount(l.taxExemptInterest), note: "interest income", targets: { interest_dividends: true, interest: true } },
  { test: (l) => hasAmount(l.ordinaryDividends), note: "dividend income", targets: { interest_dividends: true, dividends: true } },
  { test: (l) => hasAmount(l.capitalGain), note: "capital gains or losses (Schedule D or capital gain distributions)", targets: { sold_investments: true, brokerage: null } },
  { test: (l) => isYes(l.scheduleC) || hasAmount(l.businessIncome), note: "business income on Schedule C", targets: { self_employed: true } },
  { test: (l) => isYes(l.scheduleERental), note: "rental income on Schedule E", targets: { rental: true, rentals: null } },
  { test: (l) => hasAmount(l.iraDistributions) || hasAmount(l.pensions), note: "IRA, pension or annuity distributions", targets: { retirement_dist: true } },
  { test: (l) => hasAmount(l.socialSecurity), note: "Social Security benefits", targets: { social_security: true } },
  { test: (l) => isYes(l.scheduleK1), note: "partnership, S corporation or trust income (Schedule K-1)", targets: { k1: true, k1s: true } },
  { test: (l) => hasAmount(l.mortgageInterest), note: "home mortgage interest on Schedule A", targets: { mortgage: true, mortgages: null } },
  { test: (l) => hasAmount(l.studentLoanInterest), note: "student loan interest", targets: { student_loan: true } },
  { test: (l) => isYes(l.hsa), note: "a Health Savings Account (Form 8889)", targets: { hsa: true } },
  { test: (l) => isYes(l.education), note: "education credits (Form 8863)", targets: { education: true, students: null } },
  { test: (l) => hasAmount(l.estimatedPayments), note: "estimated tax payments", targets: { estimated_payments: null, estimates: null } },
  {
    test: (l) => hasAmount(l.refund) && isYes(l.directDeposit),
    note: "your refund was paid by direct deposit",
    targets: { refund_method: () => "direct_deposit", refund_how: () => "direct_deposit" },
  },
];

/** An organizer for an individual return (Form 1040): it asks for the filing or marital status. */
export function isIndividualReturnOrganizer(def: OrganizerDefinition): boolean {
  return def.sections.some((s) => s.questions.some((q) => q.key === "filing_status"));
}

/**
 * Answers and notes from the return for this organizer: a question gets an answer only if it is in
 * the organizer, carries forward (`prefill`), would be shown, is unanswered, and the answer fits it;
 * a note is kept for every shown question the return speaks to.
 */
export function organizerFromReturn(def: OrganizerDefinition, lines: ReturnLines, current: Answers, returnYear: number, cleared: ReadonlySet<string> = new Set()): { answers: Answers; hints: ReturnHint[] } {
  const byKey = new Map(def.sections.flatMap((s) => s.questions).map((q) => [q.key, q]));
  const answers: Answers = {};
  const notes = new Map<string, string[]>();
  // Rules run in order, so a parent's answer (dependents) shows its follow-ups (childcare) before they're checked.
  for (const rule of RULES) {
    if (!rule.test(lines)) continue;
    for (const [key, target] of Object.entries(rule.targets)) {
      const q = byKey.get(key);
      if (!q) continue;
      const merged = { ...current, ...answers };
      if (!visibleQuestions(def, merged).some((x) => x.key === key)) continue;
      let a = !q.prefill ? undefined : target === true ? true : typeof target === "function" ? target(lines, q) : undefined;
      const isOption = (v: AnswerValue | undefined) => (q.options ?? []).some((o) => o.value === v);
      if (q.type === "choice" && typeof a === "string" && !isOption(a)) a = CHOICE_ALIASES[a];
      const fits = a !== undefined && (q.type === "yes_no" ? typeof a === "boolean" : q.type === "choice" ? isOption(a) : false);
      // A key the client cleared after an earlier read is never filled again.
      if (fits && !cleared.has(key) && (merged[key] === undefined || merged[key] === "")) answers[key] = a!;
      notes.set(key, [...(notes.get(key) ?? []), rule.note]);
    }
  }
  const hints = [...notes].map(([key, n]) => ({ key, note: `Your ${returnYear} return shows ${n.join(" and ")}.` }));
  return { answers, hints };
}
